// src/utils/colorDetect.js — Maxula
// Détection automatique de la couleur dominante d'une image, et association
// gloutonne des images aux couleurs de variantes (perceptuelle, espace Lab).
//
// API :
//   dominantColorFromUrl(url)            -> Promise<{r,g,b}|null>   (canvas)
//   assignColorsToImages(names, cols, hx)-> { map:{couleur:index}, unresolved:[] }
//   colorNameToRgb(name, extraHexMap)    -> {r,g,b}|null
//   deltaE(rgb1, rgb2)                   -> number
//
// Aucune dépendance externe. Repli silencieux si une image n'est pas analysable
// (canvas « tainted » cross-origin, etc.).

// Couleurs nommées (FR) -> hex. Base alignée sur les presets du formulaire,
// étendue avec des couleurs courantes (Bordeaux, Marine, Crème…).
const COLOR_HEX_BASE = {
  Noir: '#1a1a1a', Blanc: '#f4f4f4', Gris: '#9aa0a6', Rouge: '#e21b1b', Bleu: '#1f6fe0',
  Vert: '#1faa45', Jaune: '#f5c518', Rose: '#f0399a', Orange: '#f5731f', Marron: '#7a4a23',
  Beige: '#d9c3a0', Violet: '#8b3fd6',
  Bordeaux: '#7b1e2b', Marine: '#14306e', Bleumarine: '#14306e', Turquoise: '#16b8a6',
  Kaki: '#6b7256', Corail: '#fb6f6f', Doré: '#d4af37', Argent: '#c0c0c0',
  Creme: '#f3e9d2', Blanccasse: '#f5f0e6', Camel: '#c19a6b', Fuchsia: '#e034c6', Cyan: '#18bcd4',
};

function hexToRgb(hex) {
  if (!hex) return null;
  let h = String(hex).replace('#', '').trim();
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (h.length !== 6) return null;
  const n = parseInt(h, 16);
  if (Number.isNaN(n)) return null;
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

const _norm = (name) => String(name || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[\s\-_]/g, '');

// Nom de couleur (FR, casse/espaces/accents indifférents) -> rgb. null si inconnu.
export function colorNameToRgb(name, extraHexMap) {
  const all = { ...COLOR_HEX_BASE, ...(extraHexMap || {}) };
  const key = _norm(name);
  for (const k of Object.keys(all)) { if (_norm(k) === key) return hexToRgb(all[k]); }
  return null;
}

// sRGB -> Lab (distance perceptuelle correcte beige/blanc/gris/rouge).
function rgbToLab(r, g, b) {
  let R = r / 255, G = g / 255, B = b / 255;
  R = R > 0.04045 ? Math.pow((R + 0.055) / 1.055, 2.4) : R / 12.92;
  G = G > 0.04045 ? Math.pow((G + 0.055) / 1.055, 2.4) : G / 12.92;
  B = B > 0.04045 ? Math.pow((B + 0.055) / 1.055, 2.4) : B / 12.92;
  const X = (R * 0.4124 + G * 0.3576 + B * 0.1805) / 0.95047;
  const Y = (R * 0.2126 + G * 0.7152 + B * 0.0722) / 1.0;
  const Z = (R * 0.0193 + G * 0.1192 + B * 0.9505) / 1.08883;
  const f = (t) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
  const fx = f(X), fy = f(Y), fz = f(Z);
  return { L: 116 * fy - 16, a: 500 * (fx - fy), b: 200 * (fy - fz) };
}

export function deltaE(rgb1, rgb2) {
  if (!rgb1 || !rgb2) return Infinity;
  const l1 = rgbToLab(rgb1.r, rgb1.g, rgb1.b);
  const l2 = rgbToLab(rgb2.r, rgb2.g, rgb2.b);
  return Math.sqrt((l1.L - l2.L) ** 2 + (l1.a - l2.a) ** 2 + (l1.b - l2.b) ** 2);
}

function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  const d = max - min;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d) + (g < b ? 6 : 0);
    else if (max === g) h = ((b - r) / d) + 2;
    else h = ((r - g) / d) + 4;
    h *= 60;
  }
  return { h, s, l };
}

// Distance couleur « intelligente » :
// - couleurs CHROMATIQUES (saturées, ni trop sombres ni trop claires) -> dominée par la TEINTE
//   (sépare correctement cyan / bleu / violet / rose, robuste à la saturation des produits) ;
// - couleurs NEUTRES (noir/blanc/gris/beige) -> distance perceptuelle Lab (ΔE).
export function colorDistance(rgb1, rgb2) {
  if (!rgb1 || !rgb2) return Infinity;
  const a = rgbToHsl(rgb1.r, rgb1.g, rgb1.b);
  const b = rgbToHsl(rgb2.r, rgb2.g, rgb2.b);
  const chromatic = a.s > 0.18 && b.s > 0.18 && a.l > 0.12 && a.l < 0.95 && b.l > 0.12 && b.l < 0.95;
  if (chromatic) {
    let dh = Math.abs(a.h - b.h); if (dh > 180) dh = 360 - dh; // [0,180]
    const ds = Math.abs(a.s - b.s), dl = Math.abs(a.l - b.l);
    return (dh / 180) * 90 + ds * 30 + dl * 30;
  }
  return deltaE(rgb1, rgb2);
}

// Couleur dominante d'une image (URL/blob) via canvas, en atténuant un fond clair (studio).
export function dominantColorFromUrl(url) {
  return new Promise((resolve) => {
    if (!url || typeof document === 'undefined') return resolve(null);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const S = 48;
        const cv = document.createElement('canvas');
        cv.width = S; cv.height = S;
        const ctx = cv.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, S, S);
        const data = ctx.getImageData(0, 0, S, S).data;

        // 1) couleur de fond ~ moyenne des pixels de bordure
        let br = 0, bg = 0, bb = 0, bn = 0;
        for (let y = 0; y < S; y++) for (let x = 0; x < S; x++) {
          if (!(x === 0 || y === 0 || x === S - 1 || y === S - 1)) continue;
          const i = (y * S + x) * 4;
          if (data[i + 3] < 128) continue;
          br += data[i]; bg += data[i + 1]; bb += data[i + 2]; bn++;
        }
        const bgc = bn ? { r: br / bn, g: bg / bn, b: bb / bn } : null;
        const bgLight = bgc && (bgc.r + bgc.g + bgc.b) / 3 > 205;

        // 2) histogramme quantifié, fond clair écarté
        const STEP = 24;
        const buckets = new Map();
        let kept = 0;
        for (let i = 0; i < data.length; i += 4) {
          if (data[i + 3] < 128) continue;
          const r = data[i], g = data[i + 1], b = data[i + 2];
          if (bgLight) {
            const dB = Math.abs(r - bgc.r) + Math.abs(g - bgc.g) + Math.abs(b - bgc.b);
            if (dB < 45) continue;
          }
          const key = `${Math.round(r / STEP)},${Math.round(g / STEP)},${Math.round(b / STEP)}`;
          const cur = buckets.get(key) || { r: 0, g: 0, b: 0, n: 0 };
          cur.r += r; cur.g += g; cur.b += b; cur.n++;
          buckets.set(key, cur);
          kept++;
        }
        let best = null;
        for (const v of buckets.values()) if (!best || v.n > best.n) best = v;

        // Vêtement clair sur fond clair -> repli sur la moyenne globale (sans exclusion)
        if (!best || kept < 12) {
          const acc = { r: 0, g: 0, b: 0, n: 0 };
          for (let i = 0; i < data.length; i += 4) {
            if (data[i + 3] < 128) continue;
            acc.r += data[i]; acc.g += data[i + 1]; acc.b += data[i + 2]; acc.n++;
          }
          return resolve(acc.n ? { r: acc.r / acc.n, g: acc.g / acc.n, b: acc.b / acc.n } : null);
        }
        resolve({ r: best.r / best.n, g: best.g / best.n, b: best.b / best.n });
      } catch (e) { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

// Association gloutonne : chaque couleur -> meilleure image (distincte tant que possible).
// names: string[] ; imageColors: ({r,g,b}|null)[] ; extraHexMap?: {}
// -> { map: {couleur: index}, unresolved: string[], confidence: {couleur: 0..1}, lowConfidence: string[] }
const CONF_MAX_DE = 45; // distance au-delà de laquelle la confiance tombe à 0 (échelle colorDistance)
const CONF_MIN = 0.62;  // seuil « incertain »
export function assignColorsToImages(names, imageColors, extraHexMap) {
  const colorNames = Array.isArray(names) ? names : [];
  const cols = Array.isArray(imageColors) ? imageColors : [];
  const targets = {};
  const pairs = [];
  colorNames.forEach((name) => {
    const rgb = colorNameToRgb(name, extraHexMap);
    targets[name] = rgb;
    if (!rgb) return;
    cols.forEach((ic, idx) => {
      if (!ic) return;
      pairs.push({ name, idx, d: colorDistance(rgb, ic) });
    });
  });
  pairs.sort((a, b) => a.d - b.d);
  const map = {};
  const bestD = {};
  const usedImg = new Set();
  const doneColor = new Set();
  // 1er passage : 1 image distincte par couleur
  for (const p of pairs) {
    if (doneColor.has(p.name) || usedImg.has(p.idx)) continue;
    map[p.name] = p.idx; bestD[p.name] = p.d; doneColor.add(p.name); usedImg.add(p.idx);
  }
  // 2e passage : plus de couleurs que d'images -> autoriser la réutilisation
  for (const p of pairs) {
    if (doneColor.has(p.name)) continue;
    map[p.name] = p.idx; bestD[p.name] = p.d; doneColor.add(p.name);
  }
  const confidence = {};
  const lowConfidence = [];
  colorNames.forEach((n) => {
    if (!(n in map)) return;
    const c = Math.max(0, Math.min(1, 1 - (bestD[n] || 0) / CONF_MAX_DE));
    confidence[n] = Math.round(c * 100) / 100;
    if (confidence[n] < CONF_MIN) lowConfidence.push(n);
  });
  const unresolved = colorNames.filter((n) => !(n in map));
  return { map, unresolved, confidence, lowConfidence };
}
